import React, { useState, useEffect } from 'react';
import { theme } from '../utils/theme';
import { subscribeSkillCategories, subscribeAllStudents, saveUserSkill, deleteUserSkill, subscribeUserSkills, saveSkillCategory } from '../firebase/database';
import { useAuth } from '../context/AuthContext';

const C = theme.colors;

export default function SkillsManagement() {
  const { userData } = useAuth();
  const [categories, setCategories] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [userSkills, setUserSkills] = useState([]);
  const [editingSkill, setEditingSkill] = useState(null);
  const [newSkill, setNewSkill] = useState({ categoryId: '', name: '', description: '', targetHours: 0, hoursSpent: 0, status: 'not-started' });
  const [newCategory, setNewCategory] = useState({ label: '', color: '#3D7EFF' });

  useEffect(() => {
    const unsubCat = subscribeSkillCategories(setCategories);
    const unsubStudents = subscribeAllStudents(data => setStudents(data.filter(s => s.status !== 'inactive')));
    return () => {
      unsubCat();
      unsubStudents();
    };
  }, []);

  useEffect(() => {
    if (students.length > 0 && !selectedStudentId) {
      const defaultStudent = students.find(s => s.name?.includes("Pankaj Ojha"));
      if (defaultStudent) setSelectedStudentId(defaultStudent.id);
      else setSelectedStudentId(students[0].id);
    }
  }, [students, selectedStudentId]);

  useEffect(() => {
    if (selectedStudentId) {
      const unsub = subscribeUserSkills(selectedStudentId, setUserSkills);
      return unsub;
    }
  }, [selectedStudentId]);

  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });

  const handleAddCategory = async () => {
    if (!newCategory.label) return;
    await saveSkillCategory({ ...newCategory, id: Date.now().toString() });
    setNewCategory({ label: '', color: '#3D7EFF' });
  };

  const handleAddSkill = async () => {
    if (!newSkill.name || !newSkill.categoryId) return;
    await saveUserSkill(selectedStudentId, newSkill);
    setNewSkill({ categoryId: '', name: '', description: '', targetHours: 0, hoursSpent: 0, status: 'not-started' });
  };

  const handleSaveSkill = async (skillData) => {
    await saveUserSkill(selectedStudentId, skillData);
    setEditingSkill(null);
  };

  const [searchTerm, setSearchTerm] = useState('');
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const filteredSkills = userSkills.filter(s => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (categories.find(c => c.id === s.categoryId)?.label || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedSkills = [...filteredSkills].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'ascending' ? -1 : 1;
    if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'ascending' ? 1 : -1;
    return 0;
  });

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '20px', fontFamily: theme.fonts.sans }}>
      <h1 style={{ fontSize: 24, fontWeight: 800, color: C.text, marginBottom: 20 }}>Skills Management</h1>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <div style={{ background: C.card, padding: 20, borderRadius: 12, border: `1px solid ${C.border}` }}>
          <h3 style={{ fontSize: 16, marginBottom: 10 }}>Select Person</h3>
          <select 
            value={selectedStudentId} 
            onChange={e => setSelectedStudentId(e.target.value)}
            style={{ padding: '10px', borderRadius: 8, border: `1px solid ${C.border}`, width: '100%' }}
          >
            {students.map(s => <option key={s.id} value={s.id}>{s.name} ({s.roll})</option>)}
          </select>
        </div>
        <div style={{ background: C.card, padding: 20, borderRadius: 12, border: `1px solid ${C.border}` }}>
          <h3 style={{ fontSize: 16, marginBottom: 10 }}>Add Category</h3>
          <div style={{ display: 'flex', gap: 10 }}>
            <input placeholder="Category Name" value={newCategory.label} onChange={e => setNewCategory({...newCategory, label: e.target.value})} style={{ padding: 10, borderRadius: 8, border: `1px solid ${C.border}`, flexGrow: 1 }} />
            <button onClick={handleAddCategory} style={{ padding: '10px 20px', borderRadius: 8, background: C.accent, color: 'white', border: 'none', cursor: 'pointer' }}>Add</button>
          </div>
        </div>
      </div>

      {selectedStudentId && (
        <div style={{ background: C.card, padding: 20, borderRadius: 12, border: `1px solid ${C.border}` }}>
          <h2 style={{ fontSize: 18, marginBottom: 15 }}>Manage Skills for {students.find(s => s.id === selectedStudentId)?.name}</h2>
          
          <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
            <input placeholder="Search skills..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ padding: 10, borderRadius: 8, border: `1px solid ${C.border}`, flexGrow: 1 }} />
            <select value={newSkill.categoryId} onChange={e => setNewSkill({...newSkill, categoryId: e.target.value})} style={{ padding: 10, borderRadius: 8 }}>
              <option value="">Select Category</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            <input placeholder="Skill Name" value={newSkill.name} onChange={e => setNewSkill({...newSkill, name: e.target.value})} style={{ padding: 10, borderRadius: 8 }} />
            <input type="number" placeholder="Target Hours" value={newSkill.targetHours} onChange={e => setNewSkill({...newSkill, targetHours: Number(e.target.value)})} style={{ padding: 10, borderRadius: 8, width: 100 }} />
            <button onClick={handleAddSkill} style={{ padding: '10px 20px', borderRadius: 8, background: C.green, color: 'white', border: 'none', cursor: 'pointer' }}>Add Skill</button>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                <th style={{ padding: '12px', fontSize: 12, color: C.muted, textTransform: 'uppercase' }}>S.No</th>
                {['categoryId', 'name', 'targetHours', 'hoursSpent', 'status'].map(key => (
                  <th key={key} onClick={() => requestSort(key)} style={{ padding: '12px', fontSize: 12, color: C.muted, textTransform: 'uppercase', cursor: 'pointer' }}>
                    {key} {sortConfig.key === key ? (sortConfig.direction === 'ascending' ? '▲' : '▼') : ''}
                  </th>
                ))}
                <th style={{ padding: '12px', fontSize: 12, color: C.muted, textTransform: 'uppercase' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedSkills.map((s, index) => (
                <tr key={s.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: '12px' }}>{index + 1}</td>
                  <td style={{ padding: '12px' }}>{categories.find(c => c.id === s.categoryId)?.label}</td>
                  <td style={{ padding: '12px', fontWeight: 600 }}>{s.name}</td>
                  <td style={{ padding: '12px' }}>{s.targetHours}h</td>
                  <td style={{ padding: '12px' }}>{s.hoursSpent}h</td>
                  <td style={{ padding: '12px' }}>
                    <span style={{ padding: '4px 8px', borderRadius: 20, fontSize: 12, background: s.status === 'completed' ? C.greenSoft : s.status === 'in-progress' ? C.amberSoft : C.dim }}>
                      {s.status}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <button onClick={() => { console.log("Edit clicked", s); setEditingSkill({...s}); }} style={{ marginRight: 5, padding: '5px 10px', borderRadius: 5, border: `1px solid ${C.border}`, cursor: 'pointer' }}>Edit</button>
                    <button onClick={() => deleteUserSkill(selectedStudentId, s.id)} style={{ padding: '5px 10px', borderRadius: 5, border: `1px solid ${C.red}`, background: C.redSoft, color: C.red, cursor: 'pointer' }}>Del</button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot style={{ background: C.bg, fontWeight: 700 }}>
              <tr>
                <td style={{ padding: '12px' }} colSpan="3">Total</td>
                <td style={{ padding: '12px' }}>{sortedSkills.reduce((sum, s) => sum + (s.targetHours || 0), 0)}h</td>
                <td style={{ padding: '12px' }}>{sortedSkills.reduce((sum, s) => sum + (s.hoursSpent || 0), 0)}h</td>
                <td colSpan="2"></td>
              </tr>
            </tfoot>
          </table>

          {editingSkill && (
            <div style={{ marginTop: 20, padding: 20, background: C.bg, borderRadius: 10 }}>
              <h4>Edit Skill: {editingSkill.name}</h4>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <input value={editingSkill.name} onChange={e => setEditingSkill({...editingSkill, name: e.target.value})} style={{ padding: 10, borderRadius: 5 }} />
                <input type="number" value={editingSkill.targetHours} onChange={e => setEditingSkill({...editingSkill, targetHours: Number(e.target.value)})} style={{ padding: 10, borderRadius: 5 }} />
                <input type="number" value={editingSkill.hoursSpent} onChange={e => setEditingSkill({...editingSkill, hoursSpent: Number(e.target.value)})} style={{ padding: 10, borderRadius: 5 }} />
                <select value={editingSkill.status} onChange={e => setEditingSkill({...editingSkill, status: e.target.value})} style={{ padding: 10, borderRadius: 5 }}>
                  <option value="not-started">Not Started</option>
                  <option value="in-progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
                <button onClick={() => handleSaveSkill(editingSkill)} style={{ padding: '10px 20px', background: C.green, color: 'white', border: 'none', borderRadius: 5 }}>Save</button>
                <button onClick={() => setEditingSkill(null)} style={{ padding: '10px 20px', background: C.muted, color: 'white', border: 'none', borderRadius: 5 }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
